/**
 * The bank upload file for a payment run (spec D8, subsystem 2).
 *
 * The manual effort this replaces is re-typing payout details into a bank
 * sheet. We produce the file; the bank moves the money. A payout API would
 * bring maker-checker, credentials and reconciliation that nobody asked for.
 *
 * Column layouts differ per bank, so the mapping is the only part that varies:
 * a format is a list of columns, each with a header and a function from a run
 * member. Adding a bank is adding an entry to `BANK_FORMATS`.
 *
 * The generic layout is the default. The ICICI and HDFC layouts below follow
 * their published bulk-upload columns and are **pending confirmation against a
 * real template** — see the backend requirements doc.
 */

import { payeeOf, NAME_UNKNOWN } from './paymentRun';
import { toCsv } from '../../../utils/csv';

const isBlank = (value) =>
  value === null || value === undefined || String(value).trim() === '';

const text = (value) => (isBlank(value) ? '' : String(value).trim());

/** Two decimals, always — a bank file is read by a machine, not a person. */
const money = (value) => (Number.isFinite(value) ? value : 0).toFixed(2);

/** What the payment is for, in the one free-text field a bank gives us. */
const narration = (member, run) =>
  [member.invoice_number || `Invoice ${member.invoice_id}`, text(run && run.reference)]
    .filter(Boolean)
    .join(' ')
    .slice(0, 50);

/**
 * Who the bank pays. `memberOf`'s display fallback does not count — a line
 * named "Unnamed" is a line with no beneficiary, and it must not reach a bank
 * file looking payable.
 */
const beneficiary = (member) => {
  const name = text((member.payee || {}).account_holder_name) || text(member.name);
  return name === NAME_UNKNOWN ? '' : name;
};

export const BANK_FORMATS = [
  {
    id: 'generic',
    label: 'Generic CSV',
    columns: [
      { header: 'Beneficiary Name', from: beneficiary },
      { header: 'Account Number', from: (m) => text((m.payee || {}).account_number) },
      { header: 'IFSC', from: (m) => text((m.payee || {}).ifsc_code) },
      { header: 'Amount', from: (m) => money(m.net) },
      { header: 'Narration', from: narration },
      { header: 'Reference', from: (m) => text(m.invoice_number) },
    ],
  },
  {
    id: 'icici',
    label: 'ICICI bulk upload',
    columns: [
      { header: 'PYMT_MODE', from: () => 'NEFT' },
      { header: 'PYMT_DATE', from: (m, run) => text(run && run.date) },
      { header: 'BENEFICIARY_NAME', from: beneficiary },
      { header: 'BENEFICIARY_ACCOUNT_NO', from: (m) => text((m.payee || {}).account_number) },
      { header: 'IFSC_CODE', from: (m) => text((m.payee || {}).ifsc_code) },
      { header: 'AMOUNT', from: (m) => money(m.net) },
      { header: 'REMARKS', from: narration },
    ],
  },
  {
    id: 'hdfc',
    label: 'HDFC bulk upload',
    columns: [
      { header: 'Transaction Type', from: () => 'NEFT' },
      { header: 'Beneficiary Name', from: beneficiary },
      { header: 'Beneficiary Account Number', from: (m) => text((m.payee || {}).account_number) },
      { header: 'IFSC', from: (m) => text((m.payee || {}).ifsc_code) },
      { header: 'Amount', from: (m) => money(m.net) },
      { header: 'Value Date', from: (m, run) => text(run && run.date) },
      { header: 'Narration', from: narration },
    ],
  },
];

export const DEFAULT_FORMAT = 'generic';

/** A format by id, falling back to the generic layout. */
export function formatById(id) {
  return (
    BANK_FORMATS.find((format) => format.id === text(id)) ||
    BANK_FORMATS.find((format) => format.id === DEFAULT_FORMAT)
  );
}

/**
 * What this member is missing before a bank could pay them, in words a person
 * can act on. Empty means the line is payable.
 */
export function missingFor(member) {
  const line = member || {};
  const payee = payeeOf(line.payee ? { payout_details: line.payee } : line);
  const missing = [];

  if (isBlank(beneficiary(line))) missing.push('beneficiary name');
  if (isBlank(payee.account_number)) missing.push('account number');
  if (isBlank(payee.ifsc_code)) missing.push('IFSC code');
  if (!(line.net > 0)) missing.push('an amount above zero');

  return missing;
}

/** The members a bank can pay, and the ones it cannot, with the reason. */
export function payableLines(run) {
  const members = (run && run.members) || [];

  return members.reduce(
    (split, member) => {
      const missing = missingFor(member);
      return missing.length === 0
        ? { ...split, lines: [...split.lines, member] }
        : {
            ...split,
            skipped: [
              ...split.skipped,
              { invoice_id: member.invoice_id, name: member.name, missing },
            ],
          };
    },
    { lines: [], skipped: [] },
  );
}

/** Header row, then one row per payable member. */
export function bankRows(run, formatId) {
  const format = formatById(formatId);
  const { lines } = payableLines(run);

  return [
    format.columns.map((column) => column.header),
    ...lines.map((member) => format.columns.map((column) => column.from(member, run))),
  ];
}

// The writer and its formula-injection guard moved to src/utils/csv.js when
// the feedback board needed the same escaping. Re-exported here so every
// existing importer of this module keeps working unchanged.
export { toCsv };

const today = () => new Date().toISOString().slice(0, 10);

/** Dated and named by format, so two downloads cannot collide. */
export function bankFileName(run, formatId) {
  const date = text(run && run.date) || today();
  return `payment-run-${date}-${formatById(formatId).id}.csv`;
}

/**
 * The file, what it came to, and what it had to leave behind.
 *
 * An empty `csv` means nothing in the run was payable — the caller shows the
 * skipped list rather than offering a download of a header row.
 */
export function bankFile(run, formatId) {
  const { lines, skipped } = payableLines(run);
  const rows = bankRows(run, formatId);

  return {
    name: bankFileName(run, formatId),
    csv: lines.length === 0 ? '' : toCsv(rows),
    count: lines.length,
    total: lines.reduce((sum, member) => sum + member.net, 0),
    skipped,
  };
}
